import axios from '@axios'

export default {
  /**
   * Upload video creative to TikTok API
   * @param {Object} params - Upload parameters
   * @param {string} params.upload_type - Upload method: UPLOAD_BY_FILE, UPLOAD_BY_URL, UPLOAD_BY_FILE_ID, UPLOAD_BY_VIDEO_ID
   * @param {File} params.video_file - Video file (required for UPLOAD_BY_FILE)
   * @param {string} params.video_url - Video URL (required for UPLOAD_BY_URL)
   * @param {string} params.file_id - File ID (required for UPLOAD_BY_FILE_ID)
   * @param {string} params.video_id - Video ID (required for UPLOAD_BY_VIDEO_ID)
   * @param {string} params.advertiser_id - Advertiser ID
   * @param {boolean} params.flaw_detect - Enable flaw detection
   * @param {boolean} params.auto_fix_enabled - Enable auto-fix
   * @param {boolean} params.auto_bind_enabled - Enable auto-bind
   * @param {string} params.video_signature - Video signature for validation
   * @param {Function} onUploadProgress - Progress callback
   */
  uploadVideoCreative(params, { onUploadProgress } = {}) {
    const formData = new FormData()

    // Add upload type
    formData.append('upload_type', params.upload_type)
    formData.append('advertiser_id', params.advertiser_id)

    // Add upload method specific parameters
    switch (params.upload_type) {
      case 'UPLOAD_BY_FILE':
        if (params.video_file) {
          formData.append('video_file', params.video_file)
        }
        break
      case 'UPLOAD_BY_URL':
        if (params.video_url) {
          formData.append('video_url', params.video_url)
        }
        break
      case 'UPLOAD_BY_FILE_ID':
        if (params.file_id) {
          formData.append('file_id', params.file_id)
        }
        break
      case 'UPLOAD_BY_VIDEO_ID':
        if (params.video_id) {
          formData.append('video_id', params.video_id)
        }
        break
      default:
        throw new Error('Invalid upload type')
    }

    // Add optional parameters
    if (params.flaw_detect !== undefined) {
      formData.append('flaw_detect', params.flaw_detect)
    }
    if (params.auto_fix_enabled !== undefined) {
      formData.append('auto_fix_enabled', params.auto_fix_enabled)
    }
    if (params.auto_bind_enabled !== undefined) {
      formData.append('auto_bind_enabled', params.auto_bind_enabled)
    }
    if (params.video_signature) {
      formData.append('video_signature', params.video_signature)
    }

    return axios.post('/api/creative/video/upload', formData, {
      onUploadProgress,
      timeout: 120000, // Reduced to 2 minutes for faster debugging
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).catch(error => {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Upload timed out after 2 minutes. The backend may be overloaded or the file is too large.')
      }

      if (error.response) {
        // Error response received
        throw new Error('Upload failed with server error. Please try again.')
      } else if (error.request) {
        throw new Error('Network error. Please check your connection and try again.')
      }

      throw error
    })
  },

  /**
   * Upload image creative to TikTok API
   * @param {Object} params - Upload parameters
   * @param {string} params.upload_type - Upload method: UPLOAD_BY_FILE, UPLOAD_BY_URL, UPLOAD_BY_FILE_ID
   * @param {File} params.image_file - Image file (required for UPLOAD_BY_FILE)
   * @param {string} params.image_url - Image URL (required for UPLOAD_BY_URL)
   * @param {string} params.file_id - File ID (required for UPLOAD_BY_FILE_ID)
   * @param {string} params.advertiser_id - Advertiser ID
   * @param {string} params.image_signature - Image signature for validation
   * @param {Function} onUploadProgress - Progress callback
   */
  uploadImageCreative(params, { onUploadProgress } = {}) {
    const formData = new FormData()

    // Map upload_type to upload_method for API compatibility
    let uploadMethod
    switch (params.upload_type) {
      case 'UPLOAD_BY_FILE':
        uploadMethod = 'file'
        break
      case 'UPLOAD_BY_URL':
        uploadMethod = 'url'
        break
      case 'UPLOAD_BY_FILE_ID':
        uploadMethod = 'file_id'
        break
      default:
        throw new Error('Invalid upload type')
    }

    // Add required upload_method parameter
    formData.append('upload_method', uploadMethod)
    formData.append('advertiser_id', params.advertiser_id)

    // Add upload method specific parameters based on API expectations
    switch (params.upload_type) {
      case 'UPLOAD_BY_FILE':
        if (params.image_file) {
          formData.append('file', params.image_file)
          // Preserve original filename - backend will handle duplicate detection
          if (params.filename) {
            // Use custom filename if provided
            formData.append('filename', params.filename)
          } else if (params.image_file.name) {
            // Use original filename from file
            formData.append('filename', params.image_file.name)
          }
        }
        break
      case 'UPLOAD_BY_URL':
        if (params.image_url) {
          formData.append('url', params.image_url)
        }
        break
      case 'UPLOAD_BY_FILE_ID':
        if (params.file_id) {
          formData.append('file_id', params.file_id)
        }
        break
      default:
        throw new Error('Invalid upload type')
    }

    // Add optional parameters
    if (params.image_signature) {
      formData.append('image_signature', params.image_signature)
    }

    return axios.post('/api/creative/image/upload', formData, {
      onUploadProgress,
      timeout: 120000, // 2 minutes timeout for image uploads
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).catch(error => {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Upload timed out. Please try again with a smaller file.')
      }

      if (error.response) {
        // Handle TikTok API specific errors
        if (error.response.status === 400 && error.response.data?.message) {
          if (error.response.data.message.includes('TikTok API')) {
            throw new Error(`TikTok API Error: ${error.response.data.message}. Please check your image format (JPG/PNG only) and size (under 50MB).`)
          }
        }
      } else if (error.request) {
        throw new Error('Network error. Please check your connection and try again.')
      }

      throw error
    })
  },

  /**
   * Upload creative with pre-review in a single operation
   * @param {Object} params - Combined upload and pre-review parameters
   * @param {string} params.type - 'video' or 'image'
   * @param {File} params.file - File to upload
   * @param {string} params.url - URL to upload from (alternative to file)
   * @param {string} params.upload_type - Upload method
   * @param {string} params.advertiser_id - Advertiser ID
   * @param {string} params.ad_text - Ad text for pre-review
   * @param {string} params.landing_page_url - Landing page URL for pre-review
   * @param {Array} params.location_codes - Location codes for pre-review
   * @param {boolean} params.is_ecommerce - Is ecommerce ad
   * @param {boolean} params.flaw_detect - Enable flaw detection (video only)
   * @param {boolean} params.auto_fix_enabled - Enable auto-fix (video only)
   * @param {boolean} params.auto_bind_enabled - Enable auto-bind (video only)
   * @param {Function} onUploadProgress - Progress callback
   */
  uploadWithPreReview(params, { onUploadProgress, cancelToken } = {}) {
    const formData = new FormData()

    // Add basic upload parameters
    formData.append('advertiser_id', params.advertiser_id)
    formData.append('upload_type', params.upload_type || 'UPLOAD_BY_FILE')

    // Add file or URL based on upload type
    if (params.file) {
      formData.append('files', params.file) // Note: 'files' for the combo endpoint
    } else if (params.url) {
      formData.append('url', params.url)
    }

    // Add pre-review parameters
    if (params.ad_text) {
      formData.append('ad_text', params.ad_text)
    }
    if (params.landing_page_url) {
      formData.append('landing_page_url', params.landing_page_url)
    }
    // Backend expects 'location' field with value 'US' (not location_codes array)
    if (params.location_codes && params.location_codes.length > 0) {
      // Backend expects singular 'location' field with string value
      formData.append('location', params.location_codes[0])
    } else {
      // Fallback: always ensure US is sent
      formData.append('location', 'US')
    }
    if (params.is_ecommerce !== undefined) {
      formData.append('is_ecommerce', params.is_ecommerce)
    }

    // Add video-specific options
    if (params.type === 'video') {
      if (params.flaw_detect !== undefined) {
        formData.append('flaw_detect', params.flaw_detect)
      }
      if (params.auto_fix_enabled !== undefined) {
        formData.append('auto_fix_enabled', params.auto_fix_enabled)
      }
      if (params.auto_bind_enabled !== undefined) {
        formData.append('auto_bind_enabled', params.auto_bind_enabled)
      }
    }

    return axios.post('/api/creative/upload-with-review', formData, {
      onUploadProgress,
      cancelToken,
      timeout: 180000, // 3 minutes for combined operation
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(response => response).catch(error => {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Combined upload and pre-review timed out. Please try again.')
      }

      if (error.response?.data?.message) {
        throw new Error(error.response.data.message)
      }

      throw error
    })
  },

  /**
   * Submit creative for pre-review
   * @param {Object} params - Pre-review parameters
   * @param {string} params.creative_id - Creative ID
   * @param {string} params.advertiser_id - Advertiser ID
   * @param {string} params.ad_text - Ad text content
   * @param {string} params.landing_page_url - Landing page URL
   * @param {Array} params.location_codes - Array of location codes
   * @param {boolean} params.is_ecommerce - Is ecommerce ad
   */
  submitPreReview(params) {
    return axios.post('/api/pre-review/create', {
      creative_id: params.creative_id,
      advertiser_id: params.advertiser_id,
      ad_text: params.ad_text,
      landing_page_url: params.landing_page_url,
      location_codes: params.location_codes,
      is_ecommerce: params.is_ecommerce,
    })
  },

  /**
   * Get creative upload status
   * @param {string} creativeId - Creative ID
   */
  getCreativeStatus(creativeId) {
    return axios.get(`/api/creatives/${creativeId}/status`)
  },

  /**
   * Get uploaded creatives list
   * @param {Object} params - Query parameters
   * @param {string} params.advertiser_id - Advertiser ID
   * @param {number} params.page - Page number
   * @param {number} params.limit - Page size (renamed from page_size)
   * @param {string} params.status - Filter by status
   * @param {string} params.type - Filter by type (video/image)
   * @param {string} params.pre_review_status - Filter by pre-review status
   * @param {string} params.search - Search term
   * @param {string} params.sort_by - Sort field
   * @param {boolean} params.sort_desc - Sort descending
   * @param {string} params.date_from - Date range start
   * @param {string} params.date_to - Date range end
   */
  getCreatives(params) {
    return axios.get('/api/creative/list', { params })
  },

  /**
   * Get creative details by ID
   * @param {string} creativeId - Creative ID
   */
  getCreativeDetails(creativeId) {
    return axios.get(`/api/creatives/${creativeId}`)
  },

  /**
   * Get creative image/thumbnail by ID
   * @param {string} creativeId - Creative ID
   */
  getCreativeImage(creativeId) {
    return `/api/creative/image/${creativeId}`
  },

  /**
   * Get video URL by ID
   * @param {string} creativeId - Creative ID
   */
  getVideoUrl(creativeId) {
    return `/api/creative/video/${creativeId}`
  },

  /**
   * Get video thumbnail by ID
   * @param {string} creativeId - Creative ID
   */
  getVideoThumbnail(creativeId) {
    return `/api/creative/video/thumbnail/${creativeId}`
  },

  /**
   * Delete creative
   * @param {string} creativeId - Creative ID
   */
  deleteCreative(creativeId) {
    return axios.delete(`/api/creatives/${creativeId}`)
  },

  /**
   * Bulk delete creatives
   * @param {Array} creativeIds - Array of creative IDs
   */
  bulkDeleteCreatives(creativeIds) {
    return axios.post('/api/creatives/bulk-delete', {
      creative_ids: creativeIds,
    })
  },

  /**
   * Resubmit creative for pre-review
   * @param {Object} params - Resubmission parameters
   * @param {string} params.creative_id - Creative ID
   * @param {string} params.advertiser_id - Advertiser ID
   * @param {string} params.ad_text - Updated ad text
   * @param {string} params.landing_page_url - Updated landing page URL
   * @param {Array} params.location_codes - Updated location codes
   * @param {boolean} params.is_ecommerce - Updated ecommerce flag
   */
  resubmitPreReview(params) {
    return axios.post(`/api/creatives/${params.creative_id}/resubmit`, {
      advertiser_id: params.advertiser_id,
      ad_text: params.ad_text,
      landing_page_url: params.landing_page_url,
      location_codes: params.location_codes,
      is_ecommerce: params.is_ecommerce,
    })
  },

  /**
   * Get pre-review status for creative
   * @param {string} creativeId - Creative ID
   */
  getPreReviewStatus(creativeId) {
    return axios.get(`/api/creatives/${creativeId}/pre-review-status`)
  },

  /**
   * Get pre-review reject reasons
   * @param {string} creativeId - Creative ID
   */
  getRejectReasons(creativeId) {
    return axios.get(`/api/creatives/${creativeId}/reject-reasons`)
  },

  /**
   * Get task history for pre-review tasks
   * @param {Object} params - Query parameters
   * @param {string} params.advertiser_id - Advertiser ID
   * @param {number} params.page - Page number
   * @param {number} params.limit - Page size
   * @param {string} params.status - Filter by status (pending, processing, completed, failed)
   * @param {string} params.sort_by - Sort field
   * @param {boolean} params.sort_desc - Sort descending
   */
  getTaskHistory(params) {
    // Ensure advertiser_id is present and not undefined/null
    if (!params.advertiser_id) {
      throw new Error('Advertiser ID is required')
    }

    // Convert to query string format and log the final URL
    const queryParams = new URLSearchParams()
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.append(key, params[key])
      }
    })

    return axios.get('/api/pre-review/history', { params })
  },

  /**
   * Get specific task status and details
   * @param {string} taskId - Task ID
   * @param {string} advertiserId - Advertiser ID
   */
  getTaskStatus(taskId, advertiserId) {
    return axios.get(`/api/pre-review/status/${taskId}`, {
      params: {
        advertiser_id: advertiserId,
      },
    })
  },

  /**
   * Sync task with TikTok API to get latest status
   * @param {string} taskId - Task ID
   * @param {string} advertiserId - Advertiser ID
   */
  syncTaskStatus(taskId, advertiserId) {
    return axios.post(`/api/pre-review/tasks/${taskId}/sync`, {
      advertiser_id: advertiserId,
    })
  },

  /**
   * Get detailed task information including materials
   * @param {string} taskId - Task ID
   * @param {string} advertiserId - Advertiser ID
   */
  getTaskDetails(taskId, advertiserId) {
    return axios.get(`/api/pre-review/status/${taskId}`, {
      params: {
        advertiser_id: advertiserId,
      },
    })
  },

  /**
   * Validate file before upload
   * @param {File} file - File to validate
   * @param {string} type - File type: 'video' or 'image'
   */
  validateFile(file, type) {
    // Ensure file exists
    if (!file) {
      return { isValid: false, errors: ['No file provided'] }
    }

    const validationRules = {
      video: {
        maxSize: 500 * 1024 * 1024, // 500MB
        allowedFormats: ['.mp4', '.mov', '.mpeg', '.avi'],
        mimeTypes: ['video/mp4', 'video/quicktime', 'video/mpeg', 'video/avi', 'video/x-msvideo'],
      },
      image: {
        maxSize: 50 * 1024 * 1024, // 50MB
        allowedFormats: ['.jpg', '.jpeg', '.png'],
        mimeTypes: ['image/jpeg', 'image/png'],
      },
    }

    const rules = validationRules[type]
    if (!rules) {
      return { isValid: false, errors: ['Invalid file type specified'] }
    }

    // Check file size
    if (file.size > rules.maxSize) {
      const maxSizeMB = rules.maxSize / (1024 * 1024)
      return { isValid: false, errors: [`File size exceeds ${maxSizeMB}MB limit`] }
    }

    // Check MIME type - be more lenient with empty mime types
    const fileMimeType = file.type || ''

    // If MIME type is empty, rely on file extension check only
    if (fileMimeType && !rules.mimeTypes.includes(fileMimeType)) {
      const formatGuide = type === 'video'
        ? 'Please use MP4, MOV, MPEG, or AVI video files'
        : 'Please use JPG, JPEG, or PNG image files'
      return { isValid: false, errors: [`Invalid file format (detected: ${fileMimeType}). ${formatGuide}`] }
    }

    // Check file extension
    const fileName = file.name.toLowerCase()
    const hasValidExtension = rules.allowedFormats.some(ext => fileName.endsWith(ext))

    if (!hasValidExtension) {
      return { isValid: false, errors: [`Invalid file extension. Allowed formats: ${rules.allowedFormats.join(', ')}`] }
    }

    return { isValid: true, errors: [] }
  },
}
